import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, errorText } from '../api/http';
import type { Me } from '../api/types';
import { setMe, signedIn, useChat } from '../store/chat';

function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/icons/icon-192.png" alt="" width={56} height={56} />
          <span>Evo</span>
        </div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

/** Keeps the field in international format: digits, spaces and a leading "+". */
const cleanPhone = (v: string) => {
  const s = v.replace(/[^\d+\s()-]/g, '');
  return s && !s.startsWith('+') ? `+${s}` : s;
};

export function LoginPage() {
  const nav = useNavigate();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, run } = useSubmit();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { user, passwordReset } = await api<{ user: Me; passwordReset?: boolean }>('POST', '/api/auth/login', { login, password });
      await signedIn(user);
      // Signed in with the password from the admin: offer to set an own one right away.
      if (passwordReset) nav('/settings', { replace: true, state: { tempPassword: password } });
      else nav('/', { replace: true });
    });
  };

  return (
    <AuthLayout title="Вход">
      <form onSubmit={submit} className="form">
        <label>
          Номер телефона или ник
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder="+380 50 123 45 67"
            autoComplete="username"
            autoCapitalize="none"
            required
            autoFocus
          />
        </label>
        <label>
          Пароль
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary" disabled={busy}>
          {busy ? 'Вход…' : 'Войти'}
        </button>
        <Link className="forgot-link" to="/forgot" state={{ phone: /^[+\d][\d\s()-]{6,}$/.test(login.trim()) ? login.trim() : '' }}>
          Забыли пароль?
        </Link>
      </form>
      <div className="auth-links">
        <span className="muted small">Нет аккаунта?</span>
        <Link to="/register">Зарегистрироваться</Link>
      </div>
    </AuthLayout>
  );
}

/**
 * "Forgot password": the server generates a new password and sends it to the messenger admin (with the name
 * and phone), who checks who is asking and passes it on. The answer is the same for any number.
 */
export function ForgotPasswordPage() {
  const initial = (useLocation().state as { phone?: string } | null)?.phone ?? '';
  const [phone, setPhone] = useState(cleanPhone(initial));
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api('POST', '/api/auth/reset-request', { phone });
      setSent(true);
    });
  };

  if (sent) {
    return (
      <AuthLayout title="Запрос отправлен">
        <div className="form">
          <p>
            Если номер <b>{phone}</b> зарегистрирован, администратор Evo получил для него новый пароль. Он свяжется с вами и
            передаст его.
          </p>
          <p className="muted small">
            Старый пароль продолжает работать, пока вы не войдёте с новым. После входа задайте свой пароль в настройках.
          </p>
          <Link className="btn primary" to="/login">
            Ко входу
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Восстановление пароля">
      <form onSubmit={submit} className="form">
        <p className="muted small">
          Укажите номер, на который зарегистрирован аккаунт. Новый пароль получит администратор Evo — он свяжется с вами и
          передаст его.
        </p>
        <label>
          Номер телефона
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(cleanPhone(e.target.value))}
            placeholder="+380 50 123 45 67"
            autoComplete="tel"
            required
            autoFocus
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary" disabled={busy}>
          {busy ? 'Отправляем…' : 'Восстановить пароль'}
        </button>
      </form>
      <div className="auth-links">
        <span className="muted small">Вспомнили пароль?</span>
        <Link to="/login">Войти</Link>
      </div>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const { busy, error, setError, run } = useSubmit();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== repeat) return setError('Пароли не совпадают');
    void run(async () => {
      const { user } = await api<{ user: Me }>('POST', '/api/auth/register', { phone, password });
      await signedIn(user);
      nav('/setup', { replace: true });
    });
  };

  return (
    <AuthLayout title="Регистрация">
      <form onSubmit={submit} className="form">
        <label>
          Номер телефона
          <input
            type="tel"
            inputMode="tel"
            name="phone"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(cleanPhone(e.target.value))}
            placeholder="+380 50 123 45 67"
            autoComplete="tel"
            required
            autoFocus
          />
          <small>В международном формате, с кодом страны.</small>
        </label>
        <label>
          Пароль
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} autoComplete="new-password" required />
          <small>Не менее 8 символов. Запомните его: восстановление пароля пока недоступно.</small>
        </label>
        <label>
          Повторите пароль
          <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} minLength={8} autoComplete="new-password" required />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary" disabled={busy}>
          {busy ? 'Регистрация…' : 'Зарегистрироваться'}
        </button>
      </form>
      <div className="auth-links">
        <Link to="/login">Уже есть аккаунт? Войти</Link>
      </div>
    </AuthLayout>
  );
}

/** Shown right after registration. Both fields can be set later in Settings. */
export function ProfileSetupPage() {
  const nav = useNavigate();
  const me = useChat((s) => s.me)!;
  const [name, setName] = useState(me.hasName ? me.displayName : '');
  const [username, setUsername] = useState(me.username ?? '');
  const { busy, error, run } = useSubmit();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const body: Record<string, string> = { displayName: name };
      if (username && username !== me.username) body.username = username;
      const { user } = await api<{ user: Me }>('PATCH', '/api/users/me', body);
      setMe(user);
      nav('/', { replace: true });
    });
  };

  return (
    <AuthLayout title="Как вас зовут?">
      <p className="muted">Имя видят ваши собеседники. По нику вас смогут найти в поиске.</p>
      <form onSubmit={submit} className="form">
        <label>
          Имя
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64} autoComplete="name" required autoFocus />
        </label>
        <label>
          Ник (необязательно)
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/^@/, ''))}
            pattern="[A-Za-z][A-Za-z0-9_]{2,31}"
            title="3–32 символа: латинские буквы, цифры и _; начинается с буквы"
            placeholder="например, ivan_petrov"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary" disabled={busy}>
          Продолжить
        </button>
      </form>
      <div className="auth-links">
        <button className="link-btn muted" style={{ marginLeft: 0 }} onClick={() => nav('/', { replace: true })}>
          Пропустить
        </button>
      </div>
    </AuthLayout>
  );
}
